import { useEffect, useState } from "react";
import { Star, Loader2, RefreshCw, MessageSquareText } from "lucide-react";
import OwnerLayout from "@/components/owner/OwnerLayout";
import apiClient from "@/lib/api";
import { formatReviewDate, REVIEW_STATUS_LABELS, REVIEW_STATUS_STYLES } from "@/components/review/reviewMeta";

export default function OwnerReviewsPage() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = () => {
    setLoading(true);
    setError(false);
    apiClient
      .get("/owner/reviews")
      .then((r) => setReviews(r.data || []))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  return (
    <OwnerLayout testid="owner-reviews-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Ulasan Penghuni</h1>
          <p className="mt-2 text-sm text-muted-foreground">Ulasan dari penghuni untuk property Anda (hanya dapat dilihat).</p>
        </div>
        <button onClick={load} data-testid="owner-reviews-refresh"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-primary border border-border hover:bg-secondary/60 transition-colors">
          <RefreshCw size={14} /> Muat Ulang
        </button>
      </div>

      {loading ? (
        <div className="mt-10 grid place-items-center py-16"><Loader2 className="animate-spin text-primary" size={28} /></div>
      ) : error ? (
        <div className="mt-8 rounded-2xl border border-destructive/30 bg-destructive/10 p-8 text-center" data-testid="owner-reviews-error">
          <p className="text-sm text-destructive">Gagal memuat ulasan.</p>
          <button onClick={load} className="mt-3 text-sm font-semibold text-primary hover:underline">Coba lagi</button>
        </div>
      ) : reviews.length === 0 ? (
        <div className="mt-6 rounded-2xl border border-dashed border-border bg-secondary/30 p-12 text-center" data-testid="owner-reviews-empty">
          <span className="grid place-items-center w-14 h-14 rounded-2xl bg-secondary text-primary mx-auto mb-4"><Star size={26} /></span>
          <h3 className="font-heading text-lg font-medium text-foreground">Belum ada ulasan.</h3>
          <p className="mt-2 text-sm text-muted-foreground">Ulasan penghuni akan muncul di sini setelah booking selesai.</p>
        </div>
      ) : (
        <div className="mt-6 space-y-4" data-testid="owner-reviews-list">
          {reviews.map((rev, i) => (
            <div key={rev.id} data-testid={`owner-review-${i}`} className="rounded-2xl border border-border bg-card p-5">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <h3 className="font-heading text-base font-semibold truncate">{rev.property_name || "Hunian"}</h3>
                  <p className="text-sm text-muted-foreground">{rev.customer_name || "Pengguna AVASA"} · {formatReviewDate(rev.created_at)}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full border ${REVIEW_STATUS_STYLES[rev.status] || ""}`}>
                    {REVIEW_STATUS_LABELS[rev.status] || rev.status}
                  </span>
                  <span className="inline-flex items-center gap-1 text-sm font-bold text-amber-500">
                    <Star size={15} fill="currentColor" /> {rev.overall_rating}
                  </span>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                <span>Kebersihan: <b className="text-foreground">{rev.cleanliness_rating}</b></span>
                <span>Lokasi: <b className="text-foreground">{rev.location_rating}</b></span>
                <span>Fasilitas: <b className="text-foreground">{rev.facility_rating}</b></span>
                <span>Owner: <b className="text-foreground">{rev.owner_rating}</b></span>
              </div>
              {rev.comment && (
                <p className="mt-3 text-sm text-foreground/90 leading-relaxed flex gap-2">
                  <MessageSquareText size={15} className="shrink-0 mt-0.5 text-muted-foreground" /> {rev.comment}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </OwnerLayout>
  );
}
